import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Shield, Car, Scale, Briefcase, Anchor, Heart, AlertTriangle, MapPin, Users, Clock, ArrowRight, ExternalLink } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const EverettPersonalInjuryLawyerPage = () => {
  const faqData = [
    {
      question: "What types of personal injury cases do you handle?",
      answer: "I handle car accidents, motorcycle crashes, slip and falls, work injuries, maritime injuries, wrongful death, and other cases where someone is injured due to another's negligence. If you're unsure whether your situation qualifies, I'm happy to discuss it with you."
    },
    {
      question: "Do you only take cases in Everett?",
      answer: "I represent injured clients throughout Washington State, though many of my clients are in Everett, Lynnwood, Marysville, Mukilteo, and the greater Snohomish County area. I'm happy to meet with clients wherever is most convenient."
    },
    {
      question: "How much does it cost to hire you?",
      answer: "I handle personal injury cases on a contingency fee basis. That means you pay nothing upfront, and my fee comes from your settlement. If there's no recovery, there's no fee. I explain everything clearly during our first conversation."
    },
    {
      question: "Will I be working directly with you?",
      answer: "Yes. I personally handle every case from start to finish. When you call, you reach me—not a paralegal or secretary. Your case is too important for hand-offs."
    },
    {
      question: "How long will my case take?",
      answer: "It depends on the complexity of your injuries and how reasonable the insurance company is. I'll give you a realistic timeline during our consultation and keep you updated throughout the process."
    }
  ];

  const practiceAreas = [
    {
      title: "Car Accidents",
      description: "Car crashes are the most common type of personal injury case I handle. Whether you were rear-ended, hit by a distracted driver, or involved in a multi-car collision, I can help you navigate the insurance process and fight for fair compensation.",
      icon: Car,
      link: "/car-crashes"
    },
    {
      title: "Motorcycle Crashes",
      description: "Motorcycle accidents often result in serious injuries. Insurance companies sometimes try to blame motorcyclists unfairly. I understand these cases and know how to protect your rights.",
      icon: Car,
      link: "/motorcycle-crashes"
    },
    {
      title: "Slip and Falls",
      description: "Property owners have a responsibility to maintain safe conditions. If you were injured in a fall at a store, restaurant, or other property, you may have a valid claim.",
      icon: AlertTriangle,
      link: "/slip-and-falls"
    },
    {
      title: "Work Injuries",
      description: "Workplace injuries can be complicated, involving workers' compensation and sometimes third-party liability claims. I help injured workers understand all their options.",
      icon: Briefcase,
      link: "/work-injuries"
    },
    {
      title: "Maritime Injuries",
      description: "Maritime workers have special protections under federal law. I handle Jones Act claims, maintenance and cure cases, and other maritime injury matters.",
      icon: Anchor,
      link: "/maritime-injuries"
    },
    {
      title: "Wrongful Death",
      description: "When a loved one dies due to someone else's negligence, families face both emotional devastation and practical concerns. I handle these sensitive cases with care and thoroughness.",
      icon: Heart,
      link: "/wrongful-death"
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="everett-personal-injury-lawyer-page">
      <SEOHead 
        title="Everett Personal Injury Lawyer | Amy C. Brown Law"
        description="Experienced Everett personal injury lawyer helping injured clients throughout Snohomish County. Car accidents, slip and falls, work injuries. Free consultation: (425) 675-1224."
        faqData={faqData}
        localArea={{ city: "Everett", county: "Snohomish", state: "WA" }}
        serviceData={{
          name: "Everett Personal Injury Legal Services",
          description: "Personal injury legal representation for clients in Everett, Lynnwood, Marysville, Mukilteo, and throughout Snohomish County, Washington.",
          category: "Personal Injury Law"
        }}
      />

      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Serving Snohomish County
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6 leading-tight">
              Everett Personal Injury Lawyer
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              When you're injured because of someone else's negligence, you shouldn't have to fight the insurance company alone. I represent injured clients in Everett, Lynnwood, Marysville, Mukilteo, and throughout Snohomish County.
            </p>

            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all"
                data-testid="hero-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Personal Injury Cases I Handle
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              I represent injured clients across a range of case types. Each situation is different, and I take the time to understand yours.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {practiceAreas.map((area, index) => (
              <Link 
                key={index}
                to={area.link}
                className="group bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:border-primary/30 hover:shadow-xl transition-all"
              >
                <area.icon className="w-12 h-12 text-primary mb-6" />
                <h3 className="font-heading text-xl font-semibold text-secondary mb-3 group-hover:text-primary transition-colors">
                  {area.title}
                </h3>
                <p className="text-slate-600 mb-4">
                  {area.description}
                </p>
                <span className="inline-flex items-center gap-1 text-primary font-medium">
                  Learn more <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Serving Clients Throughout Snohomish County
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              While my office is in Everett, I represent injured clients throughout the region. I'm happy to meet wherever is most convenient for you.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
            {['Everett', 'Lynnwood', 'Marysville', 'Mukilteo', 'Lake Stevens', 'Edmonds', 'Mountlake Terrace', 'Snohomish', 'Arlington', 'Monroe', 'Mill Creek', 'Bothell'].map((city, index) => (
              <div key={index} className="bg-white rounded-lg p-4 text-center shadow-sm">
                <MapPin className="w-5 h-5 text-primary mx-auto mb-2" />
                <p className="font-medium text-secondary text-sm">{city}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Me */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-secondary mb-6">
                Why Clients Choose Me
              </h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">I've Seen the Other Side</h3>
                    <p className="text-slate-600">I started my career defending insurance companies. I know their strategies, and I use that knowledge to fight for you.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">You Work Directly With Me</h3>
                    <p className="text-slate-600">I personally handle your case from start to finish. When you call, you reach me—not a paralegal or secretary.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">You'll Always Know What's Happening</h3>
                    <p className="text-slate-600">I keep you informed every step of the way. No wondering what's going on with your case.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Scale className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">No Fee Unless You Win</h3>
                    <p className="text-slate-600">You pay nothing upfront. My fee comes from your settlement—if there's no recovery, there's no fee.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-accent-soft rounded-2xl p-8">
              <h3 className="font-heading text-2xl font-bold text-secondary mb-4">
                A Personal Note
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                I understand what it's like to face unexpected tragedy. Multiple members of my family were killed in a preventable incident. I know the pain, the confusion, and the frustration of dealing with legal matters during the most difficult time of your life.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                That experience shapes how I approach every case. I treat my clients the way I would want to be treated—with honesty, respect, and genuine care for their well-being.
              </p>
              <p className="text-slate-700 leading-relaxed">
                If you're not sure whether you need a lawyer, call me. I'll give you honest advice, even if that means you don't need my services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Common Questions
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Concepts - Entity SEO */}
      <section className="py-12 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Understanding Personal Injury Law
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="https://en.wikipedia.org/wiki/Personal_injury" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Personal Injury <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Negligence" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Negligence <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Contingent_fee" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Contingency Fee <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Damages" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Legal Damages <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Learn more about these legal concepts on Wikipedia
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            Injured in Snohomish County?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Let's talk about what happened. I'll give you honest advice about your options—no charge, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EverettPersonalInjuryLawyerPage;
